/**
 * Verify Email Page
 * Email verification page using new API endpoints
 */

import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEmailVerification } from '../hooks/useEmailVerification'
import { VerificationStatus } from '../components/email-verification/VerificationStatus'
import { ResendEmailButton } from '../components/email-verification/ResendEmailButton'
import { ERROR_MESSAGES } from '../types/email-verification'
import type { VerifyEmailErrorResponse } from '../types/email-verification'

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const { verify } = useEmailVerification(token)

  // Auto-verify on mount if token exists
  useEffect(() => {
    if (token && !verify.isSuccess && !verify.isError) {
      verify.mutate()
    }
  }, [token])

  // No token in URL
  if (!token) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <VerificationStatus
            status="error"
            errorMessage="驗證連結缺少必要參數,請檢查連結是否完整"
          >
            <button onClick={() => navigate('/dashboard')} style={styles.button}>
              返回 Dashboard
            </button>
          </VerificationStatus>
        </div>
      </div>
    )
  }

  // Loading state
  if (verify.isPending) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <VerificationStatus status="loading" />
        </div>
      </div>
    )
  }

  // Success state
  if (verify.isSuccess) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <VerificationStatus status="success">
            <button onClick={() => navigate('/dashboard')} style={styles.button}>
              前往 Dashboard
            </button>
          </VerificationStatus>
        </div>
      </div>
    )
  }

  // Error state
  const error = verify.error as VerifyEmailErrorResponse | null
  const errorCode = error?.code || 'VERIFICATION_FAILED'
  const errorMessage = ERROR_MESSAGES[errorCode]

  // Special case: TOKEN_USED - show success-like message
  if (errorCode === 'TOKEN_USED') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <VerificationStatus status="success">
            <p style={styles.subText}>您的 Email 已驗證</p>
            <button onClick={() => navigate('/dashboard')} style={styles.button}>
              前往 Dashboard
            </button>
          </VerificationStatus>
        </div>
      </div>
    )
  }

  // Network error (no error code from API)
  if (!error || !error.code) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <VerificationStatus status="error" errorMessage="網路錯誤,請稍後再試">
            <button onClick={() => verify.mutate()} style={styles.button}>
              重試
            </button>
          </VerificationStatus>
        </div>
      </div>
    )
  }

  // Other error cases
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <VerificationStatus status="error" errorMessage={errorMessage}>
          {/* Show ResendEmailButton for expired/invalid tokens */}
          {(errorCode === 'TOKEN_EXPIRED' || errorCode === 'INVALID_TOKEN') && (
            <div style={styles.buttonGroup}>
              <ResendEmailButton style={styles.button} />
              <button onClick={() => navigate('/dashboard')} style={styles.secondaryButton}>
                返回 Dashboard
              </button>
            </div>
          )}

          {/* For verification failed, allow retry */}
          {errorCode === 'VERIFICATION_FAILED' && (
            <button onClick={() => verify.mutate()} style={styles.button}>
              重試
            </button>
          )}
        </VerificationStatus>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb',
    padding: '0 16px',
  },
  card: {
    maxWidth: '600px', // T033: Increased for better desktop experience
    width: '100%',
    backgroundColor: 'white',
    padding: '32px',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  button: {
    marginTop: '24px',
    padding: '8px 24px',
    backgroundColor: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  secondaryButton: {
    marginTop: '12px',
    padding: '8px 24px',
    backgroundColor: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  subText: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '16px',
  },
}
